import { ActionWithPayload, RehydrateOptions } from "@obg/ngrx-utils";
import { updateState } from "@obg/ngx.global";

import { VerificationStatus } from "../auth-transaction.model";

import { SMART_ID_AUTH_TRANSACTION_ACTION_TYPE } from "./smart-id-auth-transaction.action";
import { SmartIdAuthTransactionState, SmartIdVerificationStatusResponse } from "./smart-id-auth-transaction.model";

const INITIAL_STATE: SmartIdAuthTransactionState = {
	verificationStatus: undefined,
	shouldRememberUser: undefined,
	authenticationToken: undefined,
	lastActivePersonalNumber: undefined,
};

const REHYDRATE_OPTIONS: RehydrateOptions<SmartIdAuthTransactionState> = {
	name: "smartIdAuthTransactionReducer",
	excludeProps: [
		"verificationStatus",
		"authenticationToken",
		"shouldRememberUser",
		"verificationCode",
		"transactionId",
	],
};

export function smartIdAuthTransactionReducer(
	state = INITIAL_STATE,
	action: ActionWithPayload
): SmartIdAuthTransactionState {
	const response = action.payload as SmartIdVerificationStatusResponse;

	switch (action.type) {
		case SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatus: {
			return updateState(state, {
				verificationStatus: VerificationStatus.Pending,
				authenticationToken: undefined,
			});
		}
		case SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatusPending: {
			return updateState(state, {
				verificationStatus: response.status,
				authenticationToken: response.authenticationToken,
			});
		}
		case SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatusSuccess: {
			return updateState(state, {
				verificationStatus: response.status,
				authenticationToken: response.authenticationToken,
				lastActivePersonalNumber: keepPersonalNumber(state, response),
			});
		}
		case SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatusFail:
		case SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeVerificationFail:
		case SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransactionFail:
		case SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.cancelLastVerificationSuccess: {
			return updateState(state, {
				verificationStatus: VerificationStatus.Failed,
				authenticationToken: undefined,
			});
		}
		case SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.resetVerificationStatus: {
			return updateState(state, {
				verificationStatus: undefined,
				shouldRememberUser: undefined,
				verificationCode: undefined,
			});
		}

		case SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransaction: {
			return updateState(state, {
				shouldRememberUser: action.payload && action.payload.shouldRememberUser,
			});
		}
		case SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransactionSuccess: {
			return updateState(state, {
				verificationCode: response.verificationCode,
				transactionId: response.transactionId,
			});
		}
		case SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeVerification:
		case SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.reset: {
			return updateState(state, {
				verificationStatus: undefined,
				authenticationToken: undefined,
			});
		}
		default:
			return state;
	}
}

function keepPersonalNumber(
	state: SmartIdAuthTransactionState,
	response: SmartIdVerificationStatusResponse
): string | undefined {
	return (state.shouldRememberUser === null && !!state.lastActivePersonalNumber) || state.shouldRememberUser
		? response.personalNumber
		: undefined;
}

smartIdAuthTransactionReducer.prototype.rehydrateOptions = REHYDRATE_OPTIONS;
